import React from 'react'
import { Sidebar, Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { admCadastrosService } from '../../services/admCadastros'
import { useAuthContext } from '../../contexts/AuthContext'
import type { AdmCategoria, AdmCadastroInput, AdmCentroCusto } from '../../types/admCadastros'
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

type ActiveTab = 'categorias' | 'centros_custo'

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('pt-BR')
}

const EMPTY_INPUT: AdmCadastroInput = { name: '', description: '', ai_examples: [] }

const AdmCadastros: React.FC = () => {
  const { user } = useAuthContext()
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('categorias')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)

  const [categorias, setCategorias] = React.useState<AdmCategoria[]>([])
  const [centrosCusto, setCentrosCusto] = React.useState<AdmCentroCusto[]>([])

  const [filterCategorias, setFilterCategorias] = React.useState('')
  const [filterCentrosCusto, setFilterCentrosCusto] = React.useState('')

  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)

  // Modal de cadastro/edição
  const [formModal, setFormModal] = React.useState<{
    type: 'categorias' | 'centros_custo'
    data: AdmCadastroInput
  } | null>(null)
  const [aiExampleDraft, setAiExampleDraft] = React.useState('')
  const [submittingForm, setSubmittingForm] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  // Modal de exclusão lógica
  const [deleteModal, setDeleteModal] = React.useState<{
    type: 'categorias' | 'centros_custo'
    id: string
    label: string
  } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const { hasPermission: canInsert } = usePermission('adm_cadastros', 'insert')
  const { hasPermission: canUpdate } = usePermission('adm_cadastros', 'update')
  const { hasPermission: canDelete } = usePermission('adm_cadastros', 'delete')

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [cats, centros] = await Promise.all([
        admCadastrosService.listCategorias(),
        admCadastrosService.listCentrosCusto(),
      ])
      setCategorias(cats)
      setCentrosCusto(centros)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName('adm_cadastros')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'ADM • Cadastros',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  // ── Form modal ──────────────────────────────────────────────────────────────

  const openNew = (type: ActiveTab) => {
    setFormError(null)
    setAiExampleDraft('')
    setFormModal({ type, data: { ...EMPTY_INPUT } })
  }

  const openEdit = (type: ActiveTab, item: AdmCategoria | AdmCentroCusto) => {
    setFormError(null)
    setAiExampleDraft('')
    setFormModal({
      type,
      data: {
        id: item.id,
        name: item.name,
        description: item.description ?? '',
        ai_examples: item.ai_examples ?? [],
      },
    })
  }

  const closeFormModal = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
    setAiExampleDraft('')
  }

  const addAiExample = () => {
    const value = aiExampleDraft.trim()
    if (!value || !formModal) return

    const exists = formModal.data.ai_examples.some(
      (item) => item.toLocaleLowerCase('pt-BR') === value.toLocaleLowerCase('pt-BR')
    )
    if (exists) {
      setFormError('Este exemplo já foi adicionado.')
      return
    }

    setFormModal((prev) =>
      prev
        ? { ...prev, data: { ...prev.data, ai_examples: [...prev.data.ai_examples, value] } }
        : prev
    )
    setAiExampleDraft('')
    setFormError(null)
  }

  const removeAiExample = (indexToRemove: number) => {
    setFormModal((prev) =>
      prev
        ? {
          ...prev,
          data: {
            ...prev.data,
            ai_examples: prev.data.ai_examples.filter((_, index) => index !== indexToRemove),
          },
        }
        : prev
    )
  }

  const handleFormSave = async () => {
    if (!formModal) return
    if (!formModal.data.name.trim()) {
      setFormError('O nome é obrigatório.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      if (formModal.type === 'categorias') {
        await admCadastrosService.upsertCategoria(formModal.data)
      } else {
        await admCadastrosService.upsertCentroCusto(formModal.data)
      }
      closeFormModal()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  // ── Delete modal ────────────────────────────────────────────────────────────

  const openDeleteModal = (type: ActiveTab, id: string, label: string) => {
    setDeleteModal({ type, id, label })
    setDeleteReason('')
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      if (deleteModal.type === 'categorias') {
        await admCadastrosService.softDeleteCategoria(deleteModal.id, deleteReason.trim(), user.id)
        setCategorias((prev) => prev.filter((c) => c.id !== deleteModal.id))
      } else {
        await admCadastrosService.softDeleteCentroCusto(deleteModal.id, deleteReason.trim(), user.id)
        setCentrosCusto((prev) => prev.filter((c) => c.id !== deleteModal.id))
      }
      closeDeleteModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const renderTable = (
    type: ActiveTab,
    items: AdmCategoria[] | AdmCentroCusto[],
    emptyLabel: string,
    newLabel: string,
    filter: string,
    setFilter: (v: string) => void,
  ) => {
    const filtered = filter.trim()
      ? items.filter((i) => i.name.toLowerCase().includes(filter.trim().toLowerCase()))
      : items

    return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
            {type === 'categorias' ? 'Categorias' : 'Centros de Custo'}
          </Heading>
          <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
            {filtered.length}{filter.trim() && filtered.length !== items.length ? ` de ${items.length}` : ''}{' '}
            {filtered.length === 1 ? 'registro' : 'registros'}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar por nome..."
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
          />
          {canInsert && (
            <Button variant="solid" size="md" onClick={() => openNew(type)}>
              <PlusIcon className="h-4 w-4" />
              {newLabel}
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <Text size="sm" color="muted">
            {filter.trim() ? `Nenhum resultado para "${filter}".` : emptyLabel}
          </Text>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Criado em</th>
                {(canUpdate || canDelete) && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {item.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={item.description ?? ''}>
                    {item.description || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {formatDate(item.created_at)}
                  </td>
                  {(canUpdate || canDelete) && (
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {canUpdate && (
                          <button
                            type="button"
                            title="Editar"
                            onClick={() => openEdit(type, item)}
                            className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            title="Excluir"
                            onClick={() => openDeleteModal(type, item.id, item.name)}
                            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName="adm_cadastros"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name || 'ADM • Cadastros'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button type="button" onClick={() => setActiveTab('categorias')} className={tabClass('categorias')}>
                    Categorias
                  </button>
                  <button type="button" onClick={() => setActiveTab('centros_custo')} className={tabClass('centros_custo')}>
                    Centros de Custo
                  </button>
                </nav>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}

                {activeTab === 'categorias' && renderTable(
                  'categorias',
                  categorias,
                  'Nenhuma categoria cadastrada.',
                  'Nova Categoria',
                  filterCategorias,
                  setFilterCategorias,
                )}

                {activeTab === 'centros_custo' && renderTable(
                  'centros_custo',
                  centrosCusto,
                  'Nenhum centro de custo cadastrado.',
                  'Novo Centro de Custo',
                  filterCentrosCusto,
                  setFilterCentrosCusto,
                )}
              </div>
            )}
          </div>
        </PermissionGuard>
      </main>

      {/* Modal de cadastro / edição */}
      {formModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {formModal.data.id
                  ? `Editar ${formModal.type === 'categorias' ? 'Categoria' : 'Centro de Custo'}`
                  : `Nova ${formModal.type === 'categorias' ? 'Categoria' : 'Centro de Custo'}`}
              </Text>
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Fechar
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.data.name}
                  onChange={(e) => setFormModal((prev) => prev ? { ...prev, data: { ...prev.data, name: e.target.value } } : prev)}
                  placeholder={formModal.type === 'categorias' ? 'Ex: Serviços de TI' : 'Ex: Marketing Digital'}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição
                </label>
                <textarea
                  rows={3}
                  value={formModal.data.description}
                  onChange={(e) => setFormModal((prev) => prev ? { ...prev, data: { ...prev.data, description: e.target.value } } : prev)}
                  placeholder="Descrição opcional..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Exemplos para IA
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={aiExampleDraft}
                    onChange={(e) => setAiExampleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addAiExample()
                      }
                    }}
                    placeholder="Ex: materiais hidráulicos"
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addAiExample}
                    disabled={!aiExampleDraft.trim()}
                  >
                    Adicionar
                  </Button>
                </div>
                {formModal.data.ai_examples.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formModal.data.ai_examples.map((example, index) => (
                      <span
                        key={`${example}-${index}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                      >
                        {example}
                        <button
                          type="button"
                          onClick={() => removeAiExample(index)}
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-100"
                          title="Remover exemplo"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <Text size="xs" color="muted" className="mt-2">
                    Nenhum exemplo adicionado.
                  </Text>
                )}
              </div>
            </div>

            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={handleFormSave} disabled={submittingForm}>
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão lógica */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              O registro não será removido do banco de dados (exclusão lógica).
            </Text>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo da exclusão <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Informe o motivo..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeDeleteModal} disabled={submittingDelete}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete || !deleteReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdmCadastros
